import { useMemo } from 'react';

import type { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';
import { useOrionSettings } from '@vimeo/orion';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const RETENTION_DAYS = 7;

type Props = {
  isOwner: boolean;
  canKeepSourceFiles?: boolean;
  videoFiles: VideoFile[];
  keepSourceFiles?: boolean;
  videoCreatedTime?: string;
  disabled?: boolean;
};

export type SourceFileBannerState =
  | { show: false }
  | { show: true; variant: 'countdown'; daysRemaining: number }
  | { show: true; variant: 'unavailable' };

export function useSourceFileBanner({
  isOwner,
  canKeepSourceFiles,
  videoFiles,
  keepSourceFiles,
  videoCreatedTime,
  disabled,
}: Props): SourceFileBannerState {
  const { settings } = useOrionSettings();

  return useMemo(() => {
    if (!settings.enable_original_download_banner || disabled || !isOwner || !canKeepSourceFiles) {
      return { show: false };
    }

    if (keepSourceFiles) {
      return { show: false };
    }

    const daysUntilDeletion = videoCreatedTime
      ? Math.ceil(
          (new Date(videoCreatedTime).getTime() + RETENTION_DAYS * MS_PER_DAY - Date.now()) /
            MS_PER_DAY,
        )
      : null;

    const sourceFile = videoFiles.find((file) => file.quality === 'source');

    if (sourceFile) {
      if (daysUntilDeletion !== null && daysUntilDeletion > 0) {
        return { show: true, variant: 'countdown', daysRemaining: daysUntilDeletion };
      }

      return { show: false };
    }

    // No source file in API response. The API hides source files when
    // keepSourceFiles is off, but the file may still exist on disk within the
    // 7-day retention window.
    if (daysUntilDeletion !== null && daysUntilDeletion > 0) {
      return { show: true, variant: 'countdown', daysRemaining: daysUntilDeletion };
    }

    return { show: true, variant: 'unavailable' };
  }, [
    settings.enable_original_download_banner,
    disabled,
    isOwner,
    canKeepSourceFiles,
    videoFiles,
    keepSourceFiles,
    videoCreatedTime,
  ]);
}
